/**
 * 通用 MQTT 调试面板：任意主题订阅 / 发布 + 收发消息日志。
 */
import { useEffect, useMemo, useState } from "react";
import { useMqtt } from "./mqtt";

interface Sub {
  pattern: string;
  qos: 0 | 1 | 2;
}

function loadSubs(): Sub[] {
  try {
    const s = JSON.parse(localStorage.getItem("mqtt-subs") ?? "");
    if (Array.isArray(s) && s.every(x => typeof x?.pattern === "string")) return s;
  } catch {
    // fallthrough
  }
  return [{ pattern: "robot/#", qos: 1 }];
}

export function GenericPanel() {
  const { connState, subscribeTopic, publishMsg, log, clearLog, muted, toggleMute } = useMqtt();
  const connected = connState === "connected";

  const [subs, setSubs] = useState<Sub[]>(loadSubs);
  const [subInput, setSubInput] = useState("");
  const [subQos, setSubQos] = useState<0 | 1 | 2>(1);

  const [pubTopic, setPubTopic] = useState("robot/dog-01/ctrl");
  const [pubPayload, setPubPayload] = useState('{\n  "cmd": "set_conf",\n  "value": 0.35\n}');
  const [pubQos, setPubQos] = useState<0 | 1 | 2>(1);
  const [retain, setRetain] = useState(false);

  const [filter, setFilter] = useState("");

  useEffect(() => {
    localStorage.setItem("mqtt-subs", JSON.stringify(subs));
  }, [subs]);

  // 订阅关系随列表变化增减（消息统一进日志，handler 留空）
  useEffect(() => {
    const unsubs = subs.map(s => subscribeTopic(s.pattern, () => {}));
    return () => unsubs.forEach(u => u());
  }, [subs, subscribeTopic]);

  const addSub = () => {
    const pattern = subInput.trim();
    if (!pattern || subs.some(s => s.pattern === pattern)) return;
    setSubs(prev => [...prev, { pattern, qos: subQos }]);
    setSubInput("");
  };

  const send = () => {
    if (!pubTopic.trim()) return;
    if (!publishMsg(pubTopic.trim(), pubPayload, pubQos, retain)) return;
  };

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const list = f ? log.filter(e => e.topic.toLowerCase().includes(f) || e.preview.toLowerCase().includes(f)) : log;
    return list.slice(0, 150);
  }, [log, filter]);

  return (
    <div className="panel">
      <div className="grid-2">
        {/* 订阅 */}
        <section className="card">
          <h3>订阅</h3>
          <div className="row">
            <input
              className="input mono grow"
              value={subInput}
              placeholder="主题过滤器，如 robot/# 或 robot/dog-01/status"
              onChange={e => setSubInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addSub()}
            />
            <select className="input" value={subQos} onChange={e => setSubQos(Number(e.target.value) as 0 | 1 | 2)}>
              <option value={0}>QoS 0</option>
              <option value={1}>QoS 1</option>
              <option value={2}>QoS 2</option>
            </select>
            <button className="btn btn-primary" disabled={!subInput.trim()} onClick={addSub}>
              订阅
            </button>
          </div>
          {subs.length === 0 && <p className="hint">暂无订阅</p>}
          <ul className="sub-list">
            {subs.map(s => (
              <li key={s.pattern}>
                <code>{s.pattern}</code>
                <span className="tag">QoS {s.qos}</span>
                <button className="btn btn-small btn-outline" onClick={() => setSubs(prev => prev.filter(x => x.pattern !== s.pattern))}>
                  退订
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* 发布 */}
        <section className="card">
          <h3>发布</h3>
          <div className="row">
            <input className="input mono grow" value={pubTopic} placeholder="主题" onChange={e => setPubTopic(e.target.value)} />
            <select className="input" value={pubQos} onChange={e => setPubQos(Number(e.target.value) as 0 | 1 | 2)}>
              <option value={0}>QoS 0</option>
              <option value={1}>QoS 1</option>
              <option value={2}>QoS 2</option>
            </select>
            <label className="check">
              <input type="checkbox" checked={retain} onChange={e => setRetain(e.target.checked)} />
              retain
            </label>
          </div>
          <textarea
            className="input mono payload-area"
            value={pubPayload}
            placeholder="payload（通常为 JSON）"
            onChange={e => setPubPayload(e.target.value)}
          />
          <div className="row end">
            <button className="btn btn-primary" disabled={!connected || !pubTopic.trim()} onClick={send}>
              发布
            </button>
            {!connected && <span className="hint">未连接 broker</span>}
          </div>
        </section>
      </div>

      {/* 日志 */}
      <section className="card">
        <div className="card-head log-toolbar">
          <h3 className="grow">消息日志</h3>
          <span className="tag">{log.length} 条 · 最新在上</span>
          <input className="input filter-input" placeholder="过滤…" value={filter} onChange={e => setFilter(e.target.value)} />
          {["/frame", "/model/chunk"].map(m => (
            <label key={m} className="check">
              <input type="checkbox" checked={muted.includes(m)} onChange={() => toggleMute(m)} />
              屏蔽 {m}
            </label>
          ))}
          <button className="btn btn-small btn-outline" onClick={clearLog}>
            清空
          </button>
        </div>
        <div className="log-list">
          {filtered.length === 0 && <div className="log-empty">暂无消息</div>}
          {filtered.map(e => (
            <div key={e.id} className="log-row">
              <span className="log-time">{new Date(e.ts).toLocaleTimeString("zh-CN", { hour12: false })}</span>
              <span className={`dir dir-${e.dir}`}>{e.dir.toUpperCase()}</span>
              <span className="log-topic mono">{e.topic || "—"}</span>
              <span className="log-preview mono" title={e.preview}>{e.preview}</span>
              {e.size > 0 && <span className="log-size">{e.size}B</span>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
